import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useCallback } from 'react';
import {
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  ViewStyle,
  Platform,
} from 'react-native';

import { colors, radius, spacing, shadows } from '../tokens';

import { DSText } from './DSText';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'accent';

interface DSButtonProps {
  title: string;
  onPress: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode | keyof typeof Ionicons.glyphMap;
  style?: ViewStyle;
  accessibilityLabel?: string;
  testID?: string;
}

export const DSButton: React.FC<DSButtonProps> = ({
  title,
  onPress,
  variant = 'primary',
  disabled = false,
  loading = false,
  icon,
  style,
  accessibilityLabel,
  testID,
}) => {
  const handlePress = useCallback(() => {
    if (!disabled && !loading) {
      onPress();
    }
  }, [disabled, loading, onPress]);

  const isDisabled = disabled || loading;
  const renderedIcon = typeof icon === 'string'
    ? <Ionicons name={icon} size={18} color={isDisabled ? colors.outline : colors.onPrimary} />
    : icon;

  if (variant === 'primary' || variant === 'accent') {
    const gradientColors = variant === 'accent' ? colors.gradientAccent : colors.gradientPrimary;
    return (
      <TouchableOpacity
        testID={testID}
        onPress={handlePress}
        disabled={isDisabled}
        activeOpacity={0.85}
        style={[styles.wrapper, isDisabled && styles.disabled, style]}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel || title}
        accessibilityState={{ disabled: isDisabled }}
      >
        <LinearGradient
          colors={isDisabled ? [colors.surfaceContainerHigh, colors.surfaceContainerHighest] : [...gradientColors]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.gradient}
        >
          {loading ? (
            <ActivityIndicator color={colors.onPrimary} size="small" />
          ) : (
            <>
              {renderedIcon}
              <DSText
                variant="label"
                color={isDisabled ? colors.outline : colors.onPrimary}
                style={styles.text}
              >
                {title}
              </DSText>
            </>
          )}
        </LinearGradient>
      </TouchableOpacity>
    );
  }

  const variantStyles = variant === 'secondary' ? styles.secondary : styles.ghost;
  const textColor = isDisabled
    ? colors.outline
    : variant === 'secondary'
      ? colors.primary
      : colors.primary;

  return (
    <TouchableOpacity
      testID={testID}
      onPress={handlePress}
      disabled={isDisabled}
      activeOpacity={0.7}
      style={[styles.wrapper, variantStyles, isDisabled && styles.disabled, style]}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel || title}
      accessibilityState={{ disabled: isDisabled }}
    >
      {loading ? (
        <ActivityIndicator color={colors.primary} size="small" />
      ) : (
        <>
          {typeof icon === 'string'
            ? <Ionicons name={icon} size={18} color={textColor} />
            : icon}
          <DSText variant="label" color={textColor} style={styles.text}>
            {title}
          </DSText>
        </>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    minHeight: 48,
    borderRadius: radius.md,
    overflow: 'hidden',
  },
  gradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing['2xl'],
    borderRadius: radius.md,
    minHeight: 48,
    gap: spacing.sm,
    ...((shadows.md || {}) as ViewStyle),
  },
  secondary: {
    backgroundColor: colors.surfaceContainerHigh,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing['2xl'],
    gap: spacing.sm,
  },
  ghost: {
    backgroundColor: colors.transparent,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing['2xl'],
    gap: spacing.sm,
  },
  disabled: {
    opacity: 0.6,
  },
  text: {
    textAlign: 'center',
    ...Platform.select({
      web: { userSelect: 'none' as any },
      default: {},
    }),
  },
});
